import React from 'react';
import { motion } from 'framer-motion';
import { Box, Typography, TextField, IconButton } from '@mui/material';
import { IconX, IconMapPin, IconSearch, IconCheck } from '@tabler/icons-react';
import { SCALE_OPTIONS } from '../../../constants/scaleOptions';
import { scaleOptionIcons } from '../../../constants/iconMappings';

/**
 * Step 3: Location/Scale Selection
 */
const Step3Location = ({
  selectedScale,
  setSelectedScale,
  location,
  setLocation,
  selectedLocation,
  setSelectedLocation,
  locationSearchTerm,
  setLocationSearchTerm,
  setStep,
  handleLocationSearch,
  handleLocationSelect,
  locations,
  isSearchingLocation,
  locationError,
  dispatch,
}) => {
  const handleScaleSelect = (scale) => {
    // If global is selected, set the location to global
    if (scale.id === 'global') {
      setLocation('Global');
      setSelectedLocation({
        id: 'global',
        name: 'Global',
        type: 'global'
      });
      // Auto-advance to Content step when Global is chosen
      setStep(4);
    } else {
      // For non-global scales, reset location
      setLocation('');
      setSelectedLocation(null);
    }

    // Always clear search term and results
    setLocationSearchTerm('');
    dispatch({ type: 'issues/clearLocations' });

    // Set the selected scale
    setSelectedScale(scale.id);
  };

  const clearSelectedLocation = (e) => {
    e.stopPropagation();
    setLocation('');
    setSelectedLocation(null);
    setLocationSearchTerm('');
  };

  return (
    <motion.div
      key="step3"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="step-content"
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '1rem' }} gutterBottom>
        Select Scale & Location
      </Typography>

      <Box className="scale-options-container">
        {SCALE_OPTIONS.map((scale) => (
          <div
            key={scale.id}
            className={`scale-option ${selectedScale === scale.id ? 'selected' : ''}`}
            data-scale={scale.id}
            onClick={() => handleScaleSelect(scale)}
          >
            <div className="scale-option-icon">
              {scaleOptionIcons[scale.id] || <IconX size={18} />}
            </div>
            <div className="scale-option-content">
              <div className="scale-option-label">{scale.label}</div>
              <div className="scale-option-description">
                {scale.description}
              </div>
            </div>
          </div>
        ))}
      </Box>

      {selectedScale === 'global' && (
        <Box
          className="selected-location-feedback"
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1.5,
            mb: 2,
            bgcolor: '#e3f2fd',
            borderRadius: 1,
            border: '1px solid #90caf9'
          }}
        >
          <IconMapPin size={18} style={{ color: '#1976d2', marginRight: 10 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#1976d2', fontWeight: 500 }}>
              Global
            </Typography>
            <Typography variant="caption" sx={{ color: '#5c93c4' }}>
              Worldwide scope
            </Typography>
          </Box>
          <IconCheck size={18} style={{ color: '#2e7d32' }} />
        </Box>
      )}

      {selectedScale && selectedScale !== 'global' && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>Enter Location</Typography>
          <TextField
            placeholder={`Search for a ${selectedScale === 'regional' ? 'state or region' : selectedScale === 'local' ? 'city or town' : selectedScale} location...`}
            variant="outlined"
            fullWidth
            size="small"
            value={locationSearchTerm}
            onChange={handleLocationSearch}
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: <IconSearch size={18} style={{ marginRight: 8, color: '#757575' }} />
            }}
            className="single-border-input"
          />

          {/* Selected location feedback */}
          {selectedLocation && (
            <Box
              className="selected-location-feedback"
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.5,
                mb: 2,
                bgcolor: '#e3f2fd',
                borderRadius: 1,
                border: '1px solid #90caf9'
              }}
            >
              <IconMapPin size={18} style={{ color: '#1976d2', marginRight: 10 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#1976d2', fontWeight: 500 }}>
                  {selectedLocation.name}
                </Typography>
                {selectedLocation.region && (
                  <Typography variant="caption" sx={{ color: '#5c93c4' }}>
                    {selectedLocation.region}
                  </Typography>
                )}
              </Box>
              <IconCheck size={18} style={{ color: '#2e7d32', marginRight: 8 }} />
              <IconButton
                size="small"
                onClick={clearSelectedLocation}
                sx={{ padding: '4px' }}
              >
                <IconX size={16} />
              </IconButton>
            </Box>
          )}

          {isSearchingLocation && (
            <Box p={2} textAlign="center">
              <Typography variant="body2">Searching locations...</Typography>
            </Box>
          )}

          {locationError && (
            <Box p={2} textAlign="center" color="error.main">
              <Typography variant="body2">{locationError}</Typography>
            </Box>
          )}

          {!isSearchingLocation && !locationError && !selectedLocation && locationSearchTerm.trim().length >= 1 && locations.length === 0 && (
            <Box p={2} textAlign="center">
              <Typography variant="body2">No locations found. Try a different search term.</Typography>
            </Box>
          )}

          {locations.length > 0 && locationSearchTerm.trim().length >= 1 && (
            <Box className="location-results-container">
              {locations.map((loc, index) => (
                <div
                  key={loc.id || index}
                  className={`location-result ${(selectedLocation && selectedLocation.id === loc.id) || location === loc.name ? 'selected' : ''}`}
                  onClick={() => handleLocationSelect(loc)}
                >
                  <span className="location-result-icon">
                    <IconMapPin size={16} />
                  </span>
                  <div className="location-result-content">
                    <span className="location-result-name">{loc.name}</span>
                    <div className="location-result-description">
                      {/* Get appropriate location type based on type property or scale */}
                      {loc.type === 'city' && 'City'}
                      {loc.type === 'town' && 'Town'}
                      {loc.type === 'village' && 'Village'}
                      {loc.type === 'state' && 'State/Province'}
                      {loc.type === 'region' && 'Region'}
                      {loc.type === 'country' && 'Country'}
                      {loc.type === 'district' && 'District'}
                      {loc.type === 'county' && 'County'}
                      {loc.type === 'locality' && 'Locality'}
                      {!loc.type && selectedScale === 'local' && 'City'}
                      {!loc.type && selectedScale === 'regional' && 'State/Province'}
                      {!loc.type && selectedScale === 'national' && 'Country'}

                      {/* Show region information when available */}
                      {loc.region && ` in ${loc.region}`}

                      {/* Show description as fallback */}
                      {loc.description && !loc.region && ` - ${loc.description}`}
                    </div>
                  </div>
                </div>
              ))}
            </Box>
          )}
        </Box>
      )}
    </motion.div>
  );
};

export default Step3Location;
